import React from 'react'
import { SurfacePalette, type } from 'pages/business/sprint-packets/tokens'

interface Props {
  palette: SurfacePalette
  label: string
  /** Tile/row-level hover reveal: hidden at 0 opacity until parent `.group:hover`. */
  hideUntilGroupHover?: boolean
  /**
   * Compact icon-only treatment for overlaying on a creative thumbnail (no
   * "Comment" label). Pair with a scrim background from the caller for contrast.
   */
  iconOnly?: boolean
  /** Optional inline style overrides merged onto the trigger button. */
  style?: React.CSSProperties
  /**
   * Direct click handler. When provided, the button stops advertising itself as a
   * delegated `data-comment-trigger` and instead fires this handler — for surfaces
   * without the `useCommentAnchors` root listener (e.g. the native audience modal).
   * Omit it inside packet anchors, where the click must bubble to the root.
   */
  onClick?: () => void
  /**
   * Number of unresolved (open) root comments on this anchor (v1.4a). When `> 0`
   * the trigger switches to an accent state and shows a count badge, and a
   * `hideUntilGroupHover` indicator stays visible (an open thread should always be
   * discoverable, not hidden until hover). Resolved-only anchors pass 0/undefined
   * and stay neutral.
   */
  unresolvedCount?: number
}

const BubbleIcon = ({ size = 12 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M14 9.5A2.5 2.5 0 0 1 11.5 12H5l-3 3V4.5A2.5 2.5 0 0 1 4.5 2h7A2.5 2.5 0 0 1 14 4.5v5Z" />
  </svg>
)

const CountBadge = ({ palette, count }: { palette: SurfacePalette; count: number }) => (
  <span
    // The count is already in the button's aria-label, so the badge is decorative.
    aria-hidden="true"
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: 15,
      height: 15,
      padding: '0 4px',
      borderRadius: 999,
      background: palette.accent,
      color: palette.accentInk,
      fontFamily: type.family.mono,
      fontSize: 10,
      fontWeight: type.weight.semibold,
      letterSpacing: 0,
      lineHeight: 1,
    }}
  >
    {count}
  </span>
)

export const CommentIndicator = ({
  palette,
  label,
  hideUntilGroupHover,
  iconOnly,
  style,
  onClick,
  unresolvedCount,
}: Props) => {
  const count = unresolvedCount ?? 0
  const hasUnresolved = count > 0
  return (
    <button
      type="button"
      // Delegated (packet) mode advertises the trigger and lets the click bubble to
      // the useCommentAnchors root; direct mode handles its own click instead.
      data-comment-trigger={onClick ? undefined : 'true'}
      onClick={onClick}
      aria-label={
        hasUnresolved ? `Comments on ${label}, ${count} unresolved` : `Comments on ${label}`
      }
      // An open thread must stay visible: only hide-until-hover when there's nothing
      // unresolved to surface.
      className={
        hideUntilGroupHover && !hasUnresolved
          ? 'opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity'
          : ''
      }
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: iconOnly ? 6 : '2px 6px',
        margin: 0,
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        // Accent the icon/label when unresolved; a caller `style.color` (e.g. the
        // white-on-scrim creative overlay) still wins via the spread below.
        color: hasUnresolved ? palette.accent : palette.textMuted,
        fontFamily: type.family.mono,
        fontSize: type.size.eyebrow,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        lineHeight: 0,
        ...style,
      }}
    >
      <BubbleIcon size={iconOnly ? 16 : 12} />
      {!iconOnly && 'Comment'}
      {hasUnresolved && <CountBadge palette={palette} count={count} />}
    </button>
  )
}
